/**
 * Decode HTML entities in text
 * Handles common entities like &#x27; (apostrophe), &amp; (ampersand), etc.
 * 
 * The server escapes HTML entities for security (XSS prevention), but we need
 * to decode them for display in the UI.
 */
export function decodeHtmlEntities(text: string): string {
  if (typeof text !== 'string') {
    return text;
  }

  // Create a temporary DOM element to decode HTML entities
  // This works in web environments (including react-native-web)
  // Using textContent instead of innerHTML for better security
  if (typeof document !== 'undefined') {
    try {
      const textarea = document.createElement('textarea');
      // Use textContent to set value safely, then read value property
      // This prevents XSS if malicious content somehow gets through
      textarea.textContent = text;
      return textarea.value || text;
    } catch (e) {
      // Fall through to manual decoding if DOM method fails
    }
  }

  // Manual decoding for React Native and as fallback
  // Decode in reverse order to avoid double-decoding (e.g., &amp; should be decoded last)
  return text
    .replace(/&#x27;/g, "'")      // Hex apostrophe
    .replace(/&#39;/g, "'")       // Decimal apostrophe
    .replace(/&apos;/g, "'")      // Named apostrophe
    .replace(/&quot;/g, '"')      // Named quote
    .replace(/&#x2F;/g, '/')      // Hex slash
    .replace(/&#47;/g, '/')       // Decimal slash
    .replace(/&lt;/g, '<')        // Less than
    .replace(/&gt;/g, '>')        // Greater than
    .replace(/&amp;/g, '&');      // Ampersand (must be last to avoid double-decoding)
}
